import { StringUtilities } from "../Common/string";
import { ConcurrencyErrorMessage, MethodNotAllowedMessage } from "./constants";
import { ToastService } from "./toast-service";
import { ValidationHelper } from "./validation-helper";

export enum HttpMethod {
    POST = "POST",
    GET = "GET",
    PUT = "PUT",
    DELETE = "DELETE"
}

export interface IHttpRequest<TRequest = {}> {
    /** The Http Method */
    Method: HttpMethod;
    /** The Data to send */
    Data?: TRequest;
    /** The Url */
    Url: string;
    /** The query names and values */
    Query?: Record<string, string>;
    /** The Toast Success Message */
    SuccessMessage?: string;
    /** Whether to supress the Success Message */
    SupressSuccessMessage?: boolean;
    /** The key for the Validation Store */
    ValidationKey?: string;
    /** The element(s?) to show validation on */
    ValidationElement?: string;
    ContentType?: string;
    DownloadFile?: boolean;
    CustomHeaders?: Headers;
}

export interface IHttpFileUploadRequest<TRequest = {}> {
    /** File object that will be uploaded */
    Filedata: any;
    /** Additional Data to send */
    OtherData?: TRequest;
    /** The Url */
    Url: string;
    /** The Toast Success Message */
    SuccessMessage?: string;
    /** Whether to supress the Success Message */
    SupressSuccessMessage?: boolean;
    /** The key for the Validation Store */
    ValidationKey?: string;
}

export interface IErrorCollection {
    isWarning: boolean;
    errorMessage: string;
    objectId: string;
}

export interface IHttpResponseData {
    [key: string]: any;
    hasError?: boolean;
    hasBoundErrors?: boolean;
    errorCollection?: IErrorCollection[];
}

export interface IHttpResponse<TResponse extends IHttpResponseData> {
    Data?: TResponse;
    Status: number;
    Headers: Headers;
    StatusText: string;
    Ok: boolean;
    AccessDenied: boolean;
}

export class HttpResponse<TResponse> implements IHttpResponse<TResponse> {
    public Data?: TResponse;
    public Status: number;
    public StatusText: string;
    public Ok: boolean;
    public Redirected: boolean = false;
    public AccessDenied: boolean = false;
    public Headers: Headers;

    // Some clever stuff, by TypeScript, allow initializing this object, with a "Partial" definition of itself
    public constructor(init?: Partial<HttpResponse<TResponse>>) {
        Object.assign(this, init);
    }
}

/**
 * A Module containing functions to assist in calling http endpoints
 */
export module HttpService {

    export function createQueryString(params: { [key: string]: any }): string {
        const queryString = Object.keys(params)
            .map(key => {
                const value = params[key];
                if (Array.isArray(value)) {
                    return value.map(val => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`).join('&');
                } else {
                    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                }
            })
            .join('&');
        return queryString;
    }

    export async function getFileAsync(url: string, data: any, buttonSelector ?: string | HTMLElement): Promise<IHttpResponse<{}>> {
        if(data != null) {
            var dataParams = $.param(data);
            data = null;
            url += "?" + dataParams;
        }
        return await sendDataAsync(HttpMethod.GET, url, data, buttonSelector, null, true, true);
    }

    /// <summary>
    /// Sends a POST request to the server that downloads a file
    /// </summary>
    export async function getFileFromServerAsPostAsync(url: string, data: any, buttonSelector?: string | HTMLElement): Promise<IHttpResponse<{}>> {
        return await sendDataAsync(HttpMethod.POST, url, data, buttonSelector, null, true, true);
    }

    export async function getDataAsync(url: string, data: any, buttonSelector ?: string | HTMLElement): Promise <IHttpResponse<{}>> {
        if(data != null) {
            var dataParams = $.param(data);
            data = null;
            url += "?" + dataParams;
        }

        return await sendDataAsync(HttpMethod.GET, url, data, buttonSelector, null, true);
    }
   
    export async function postDataAsync(url: string, data: any, buttonSelector: string | HTMLElement, validationKey: string, surpressSuccessMessage: boolean = false): Promise<IHttpResponse<{}>> {
        return await sendDataAsync(HttpMethod.POST, url, data, buttonSelector, validationKey, surpressSuccessMessage);
    }

    export async function deleteDataAsync(url: string, data: any, buttonSelector: string, validationKey: string): Promise<IHttpResponse<{}>> {
        return await sendDataAsync(HttpMethod.DELETE, url, data, buttonSelector, validationKey);
    }
    

    async function sendDataAsync(method: HttpMethod, url: string, data: any, buttonIdentifier: string | HTMLElement, validationKey: string, surpressSuccessMessage: boolean = false, downloadFile: boolean = false): Promise<IHttpResponse<{}>> {
        // Disable the button and put a spinner there
        let button: JQuery<HTMLElement> = null;
        
        if(buttonIdentifier as HTMLElement != null) {
            button = $(buttonIdentifier as HTMLElement);
        } else {
            button = $(buttonIdentifier as string);
        }

        button.prop('disabled', true);

        button.append(' <i class="fa fa-spinner fa-spin"></i>');

        var httpResponse = await callHttpService({
            Method: method,
            Url: url,
            Data: data,
            ValidationKey: validationKey,
            SupressSuccessMessage: surpressSuccessMessage,
            DownloadFile: downloadFile
        });

        // Remove the spinner
        $(button).find("i.fa-spinner").remove();

        button.prop('disabled', false);

        return httpResponse;
    }
    
    export async function postFileDataAsync(url: string, data: any, buttonSelector: string, validationKey: string): Promise<IHttpResponse<{}>> {
        // Disable the button and put a spinner there
        var button = $(buttonSelector);

        button.prop('disabled', true);

        button.append(' <i class="fa fa-spinner fa-spin"></i>');

        var httpResponse = await callHttpService({
            Method: HttpMethod.POST,
            Url: url,
            Data: data,
            ContentType: 'multipart/form-data',
            ValidationKey: validationKey
        });

        button.prop('disabled', false);

        // Remove the spinner
        $(buttonSelector + " > i.fa-spinner").remove();

        return httpResponse;
    }
    
    export function hasErrors(response: IHttpResponseData): boolean {
        return response.AccessDenied || !response.Ok || (response.Data != undefined && response.Data.hasError);
    }

    /**
     * 
     * @param request The Request to Execute
    */
    export async function call<TResponse = {}>(request: IHttpRequest): Promise<IHttpResponse<TResponse>> {
        try {
            var contentType = 'application/json';
            var body = null as BodyInit;
            var headers = new Headers({
                'X-Requested-With': 'XMLHttpRequest' // AJAX Request
            });

            if (request.ContentType == 'multipart/form-data') {
                contentType = undefined; // Browser will override this
                body = request.Data as FormData;
            }
            else {
                if (request.Data != null) {
                    body = JSON.stringify(request.Data);
                }
                headers.append('content-type', contentType);
                headers.append('accept', 'application/json');
            }

            if (request.CustomHeaders != null) {
                request.CustomHeaders.forEach((value: string, key: string, parent: Headers) => {
                    headers.append(key, value);
                });
            }

            const response = await fetch(request.Url, {
                method: request.Method,
                body: body,
                headers: headers,
                redirect: "follow"
            });

            // Check if the response status is 401 and if there is a RedirectLocation header
            if (response.status === 401 && response.headers.has('Location')) {
                const redirectUrl = response.headers.get('Location');
                // Redirect the user to the specified location
                window.location.href = redirectUrl;
            }

            if (request.DownloadFile)
                return await handleFileDownload(response);
            else
                return await handleUrlResponse(response);
        }
        catch (err) {
            // TODO: Could maybe throw and let store action handle?
        }
    }
    
    export async function callHttpService <TResponse extends IHttpResponseData = {}>(request: IHttpRequest): Promise<IHttpResponse<TResponse>> {
        ValidationHelper.RemoveFormErrors(request.ValidationKey);

        // Add on CSRF token into header if one is available
        var csrfToken = $('[name="__RequestVerificationToken"]').val();
        if(csrfToken != null) {
            request.CustomHeaders = request.CustomHeaders = request.CustomHeaders || new Headers();

            request.CustomHeaders.append('RequestVerificationToken', csrfToken as string);
        }
        
        const response = await HttpService.call<TResponse>(request);

        if (response == null) {
            ToastService.sendToast({ Message: "No Response Recieved From Server", Status: "Error", RemoveExistingToasts: true });
        } else if (response.AccessDenied) {
            ToastService.sendToast({ Message: "You don't have permissions for this operation.", Status: "Error", RemoveExistingToasts: true });
        }
        else if (!response.Ok) {
            var errorMessage = "";
            
            if (response.Status == 405)
                errorMessage = MethodNotAllowedMessage;
            else if (response.Status == 409)
                errorMessage = ConcurrencyErrorMessage
            else
                errorMessage = "Unexpected error encountered, operation failed."

            ToastService.sendToast({ Message: errorMessage, Status: "Error", RemoveExistingToasts: true });
        }
        else if (response.Data != undefined && response.Data.hasError) {
            if (request.ValidationKey) {
                ValidationHelper.ParseValidation(response.Data.errorCollection, request.ValidationKey);
            }
            else {
                // If no validation key was specified, just display a toast
                var options: ToastrOptions = {
                    closeButton: true,
                    timeOut: 0
                };
                
                response.Data.errorCollection.map(function (item, index) {
                    ToastService.sendToast({ Message: item.errorMessage, Status: "Error", Options: options, RemoveExistingToasts: index == 0 });
                });
            }
        }
        else if (request.SupressSuccessMessage !== true) {
            // Response was ok, possibly reload the page?
            ToastService.sendToast({ Message: request.SuccessMessage || "Operation completed Successfully", Status: "Success", RemoveExistingToasts: true });
        }

        return response;
    }

    async function handleUrlResponse<TResponse = {}>(response: Response): Promise<IHttpResponse<any>> {
        try {
            const fnResponse = new HttpResponse<TResponse>({
                StatusText: response.statusText,
                Ok: response.ok,
                Status: response.status,
                Headers: response.headers,
            })

            if (fnResponse.Ok && !response.redirected) {
                // Only de-serialize data if the response was a good one and the response body actually has some data.
                var bodyText = await response.text();

                if (bodyText.length > 0) {
                    fnResponse.Data = (JSON.parse(bodyText)) as TResponse;
                }
            }

            if (fnResponse.Ok && response.redirected && StringUtilities.contains(response.url, "AccessDenied")) {
                // Response is ok, however we've been redirected (due to not having the correct permissions)
                return new HttpResponse<TResponse>({
                    Ok: false,
                    StatusText: null,
                    Status: 0,
                    AccessDenied: true,
                    Headers: response.headers
                });
            }

            return fnResponse;
        }
        catch (err) {
            const errorsFromHttp = err;
            // TODO: Could maybe throw and let store action handle?
        }
    }

    async function handleFileDownload<TResponse = {}>(response: Response): Promise<IHttpResponse<any>> {
        try {
            var contentType = response.headers.get('content-type');
            const fnResponse = new HttpResponse<TResponse>({
                StatusText: response.statusText,
                Ok: response.ok,
                Status: response.status,
                Headers: response.headers,
            });

            // in some cases we dont have data so we dont return an empty file we return Http code NoContect
            if (fnResponse.Status === 204) {
                return fnResponse;
            }

            //if there is a validation error we dont get the file we get error collection as json.
            if (response.ok && contentType.lastIndexOf("json") < 0) {
                var blob = await response.blob();
                var file = window.URL.createObjectURL(blob);
                var fileNameHeader = response.headers.get('Content-Disposition');
                if (fileNameHeader == null) {
                    window.open(file);
                }
                else {
                    var fileNameHeaders = response.headers.get('Content-Disposition').split(";");
                    var startFileName = fileNameHeaders.find(x => x.indexOf("filename=") > 0);
                    var fileName = startFileName.replace("filename=", "").trim();
                    var a = document.createElement('a');
                    a.href = file;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                }
            }
            else if (response.ok && contentType.lastIndexOf("json") > 0) {
                if (fnResponse.Ok && !response.redirected) {
                    // Only de-serialize data if the response was a good one and the response body actually has some data.
                    var bodyText = await response.text();
                    if (bodyText.length > 0) {
                        fnResponse.Data = (JSON.parse(bodyText));
                    }
                }
            }
            return fnResponse;

        }
        catch (err) {
            const errorsFromHttp = err;
            // TODO: Could maybe throw and let store action handle?
        }
    }
}
